import { t } from "@lingui/macro";
import { useResponsiveProp } from "@shopify/restyle";
import graphql from "babel-plugin-relay/macro";
import { ScrollView, useWindowDimensions } from "react-native";
import { useFragment } from "react-relay";

import { useFlags } from "@/utils/launchDarkly/useFlags";
import { ToolTipIcon } from "@whoosh/design-system/src/components/icons_v2/general";
import { Tooltip } from "@whoosh/design-system/src/components/overlay/Tooltip/elements/Tooltip/Tooltip";
import {
  Text,
  VStack,
  HStack,
} from "@whoosh/design-system/src/components/primitive";
import { useGetClubCurrency } from "@whoosh/features/src/ClubCurrency";
import { ConvertCentsToDollars } from "@whoosh/utilities/src/Currency";

import { BookingCredits } from "../BookingCredits/BookingCredits";
import { BookingLoyaltyRewards } from "../BookingCredits/components/BookingLoyaltyRewards/BookingLoyaltyRewards";
import { BookingPaymentMethodSelect } from "../BookingPaymentMethodSelect";
import { BookingCharges_booking$key } from "./__generated__/BookingCharges_booking.graphql";
import { useGetSummaryCalculations } from "./hooks/useGetSummaryCalculations";

type Props = {
  bookingRef: BookingCharges_booking$key | null;
  isBookingOwner: boolean;
  showLoyaltyEnrollment?: boolean;
};

export const BookingCharges = ({
  bookingRef,
  isBookingOwner,
  showLoyaltyEnrollment = true,
}: Props) => {
  const { currencySymbol } = useGetClubCurrency();
  const { patronLoyalty } = useFlags();
  const booking = useFragment(BookingChargesFragmentString, bookingRef);
  const {
    subtotal,
    totalDiscounts,
    totalTaxes,
    total,
    totalDueNow,
    totalDueArrival,
    totalPaid,
    serviceChargeTotal,
  } = useGetSummaryCalculations({ fragmentRef: booking ?? null });
  const loyaltyOrder = booking?.patronOrders?.edges?.[0]?.node;
  const shouldShowExpectedLoyaltyPoints =
    loyaltyOrder?.availableLoyaltyRewards != null &&
    loyaltyOrder.expectedLoyaltyPoints != null;
  const expectedLoyaltyPoints = loyaltyOrder?.expectedLoyaltyPoints ?? 0;
  const loyaltyPointTerminology =
    loyaltyOrder?.availableLoyaltyRewards?.terminologyOther ?? t`Points`;

  const totalDueText = !!totalPaid
    ? t`Total paid`
    : totalDueNow !== 0
    ? t`Total due now`
    : t`Total due upon arrival`;

  const firstValueToDisplay = !!totalPaid
    ? totalPaid
    : totalDueNow !== 0
    ? totalDueNow
    : totalDueArrival;

  const acceptsCreditCard =
    booking?.startSlot?.agenda?.facility?.acceptsCreditCard ?? true;
  const showPaymentMethodSelect = isBookingOwner && acceptsCreditCard;
  const requiresPrePayment =
    booking?.paymentRequirements?.prePaymentRequirement === "FULL";

  const viewportHeight = useWindowDimensions().height;
  // Viewport height minus the padding outside of the scrollview

  const isBookingConfirmed = booking?.status === "CONFIRMED";
  const isDraftBooking = booking?.status === "DRAFT";
  const maxHeight = viewportHeight - (isBookingConfirmed ? 0 : 132) - 64;

  const scrollEnabled = useResponsiveProp({ base: 0, md: 1 }) === 1;

  return (
    <ScrollView scrollEnabled={scrollEnabled}>
      <VStack
        backgroundColor="surface-primary"
        padding={5}
        maxHeight={{ md: maxHeight }}
      >
        <Text variant="mainHeading">{t`Summary`}</Text>
        <VStack gap={4} marginTop={5} paddingBottom={6}>
          <HStack justifyContent="space-between">
            <Text variant="mainBaseMedium">{t`Subtotal`}</Text>
            <Text
              variant="mainBaseMedium"
              testID="subtotal-amount"
            >{`${currencySymbol}${ConvertCentsToDollars(subtotal)}`}</Text>
          </HStack>
          {!!totalDiscounts ? (
            <HStack justifyContent="space-between">
              <Text variant="mainBaseMedium">{t`Discounts`}</Text>
              <Text
                variant="mainBaseMedium"
                testID="discounts-amount"
              >{`-${currencySymbol}${ConvertCentsToDollars(
                totalDiscounts
              )}`}</Text>
            </HStack>
          ) : null}
          {!!serviceChargeTotal ? (
            <HStack justifyContent="space-between">
              <HStack gap={2}>
                <Text variant="mainBaseMedium">{t`Booking Fee`}</Text>
                <Text
                  color="text-secondary"
                  variant="mainBaseMedium"
                >{t`Non-Refundable`}</Text>
                <Tooltip
                  size="large"
                  trigger={<ToolTipIcon color="text-secondary" />}
                  content={
                    <Text variant="mainMicro" color={"text-inverse"}>
                      {t`The booking fee covers the cost of processing your booking. It is non-refundable.`}
                    </Text>
                  }
                />
              </HStack>
              <Text
                variant="mainBaseMedium"
                testID="service-charge-amount"
              >{`${currencySymbol}${ConvertCentsToDollars(
                serviceChargeTotal
              )}`}</Text>
            </HStack>
          ) : null}
          <HStack justifyContent="space-between">
            <Text variant="mainBaseMedium">{t`Taxes`}</Text>
            <Text
              variant="mainBaseMedium"
              testID="taxes-amount"
            >{`${currencySymbol}${ConvertCentsToDollars(totalTaxes)}`}</Text>
          </HStack>
          <HStack justifyContent="space-between">
            <Text variant="mainBaseMedium">{t`Total`}</Text>
            <Text
              variant="mainBaseMedium"
              testID="total-amount"
            >{`${currencySymbol}${ConvertCentsToDollars(total)}`}</Text>
          </HStack>

          {isBookingOwner ? (
            <BookingCredits
              bookingRef={booking ?? null}
              loyaltyBookingRef={booking ?? null}
            />
          ) : null}

          <HStack justifyContent="space-between">
            <Text testID="total-due-text" variant="mainHeading">
              {totalDueText}
            </Text>
            <Text
              variant="mainHeading"
              testID="total-due-amount"
            >{`${currencySymbol}${ConvertCentsToDollars(
              firstValueToDisplay
            )}`}</Text>
          </HStack>
          {patronLoyalty &&
          isBookingOwner &&
          showLoyaltyEnrollment &&
          requiresPrePayment &&
          isDraftBooking &&
          booking?.id ? (
            <BookingLoyaltyRewards
              bookingRef={booking ?? null}
              display="enrollment"
            />
          ) : null}
          {patronLoyalty && shouldShowExpectedLoyaltyPoints ? (
            <HStack justifyContent="space-between">
              <Text variant="mainBaseMedium">
                {t`${loyaltyPointTerminology} earned with this order`}
              </Text>
              <Text variant="mainBaseMedium" testID="expected-loyalty-points">
                {expectedLoyaltyPoints}
              </Text>
            </HStack>
          ) : null}
        </VStack>

        {showPaymentMethodSelect ? (
          <BookingPaymentMethodSelect fragmentRef={booking ?? null} />
        ) : null}
      </VStack>
    </ScrollView>
  );
};

export const BookingChargesFragmentString = graphql`
  fragment BookingCharges_booking on Booking
  @argumentDefinitions(
    patronLoyalty: { type: "Boolean!", defaultValue: false }
  ) {
    paymentRequirements {
      prePaymentRequirement
    }
    id
    __typename
    patronOrders: orders(first: 1) {
      edges {
        node {
          expectedLoyaltyPoints @include(if: $patronLoyalty)
          availableLoyaltyRewards @include(if: $patronLoyalty) {
            terminologyOther
          }
        }
      }
    }
    startSlot {
      __typename
      agenda {
        facility {
          acceptsCreditCard
        }
      }
    }
    ... on StandardBooking {
      status
    }
    ... on LessonBooking {
      status
    }
    ... on DiningBooking {
      status
    }
    ...BookingPaymentMethodSelect_booking
    ...useGetSummaryCalculations_Booking
    ...BookingCredits_booking @arguments(patronLoyalty: $patronLoyalty)
    ...BookingLoyaltyRewards_booking @arguments(patronLoyalty: $patronLoyalty)
  }
`;
